// usePairingFlow — hook compartilhado pelas telas AccountLogin/AccountSignup.
//
// Encapsula: pairing init → polling → finalize (preference/resolve + Xtream
// login). Retry funciona via incrementar `attempt` (chave dependência do
// useEffect) — sem precisar navegar fora e voltar.

import { useTranslation } from '@app-iptv/i18n'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { hydrateSourceFromAccount } from '../account-context'
import { ApiError, api } from '../api'
import { errorToMessage } from '../error-messages'
import { pollAccountSetup, pollPairingStatus } from '../services/pairing'
import { useSessionStore } from '../store'
import { loginSwipeSession } from '../sync'

export type PairingStage =
  | { kind: 'loading' }
  | { kind: 'waiting'; code: string; qrPayload: string; expiresAt: number }
  | { kind: 'finalizing' }
  | { kind: 'error'; message: string; recoverable: boolean }
  | { kind: 'needsPreference' } // approved + logado cloud, mas pref não salva
  | { kind: 'awaitingSetup' } // approved + logado, esperando o cel/site salvar a fonte

export interface UsePairingFlowResult {
  stage: PairingStage
  retry: () => void
  cancel: () => void
  /** Escape do stage 'awaitingSetup': em vez de esperar o celular, configura a
   *  fonte na própria TV. Aborta o poll e cai no fluxo logado (/onboarding/mode
   *  via RootRoute — há sessão Swipe, mas ainda sem fonte). */
  configureHere: () => void
}

export function usePairingFlow(): UsePairingFlowResult {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const setPairingFinalizing = useSessionStore((s) => s.setPairingFinalizing)

  const [stage, setStage] = useState<PairingStage>({ kind: 'loading' })
  const [attempt, setAttempt] = useState(0)
  const abortRef = useRef<AbortController | null>(null)

  const cancel = useCallback(() => {
    abortRef.current?.abort()
    // Sair do gate: libera o RootRoute pra rotear (o gate deixa de conduzir um
    // pairing). Sem isto, a guarda `pairingFinalizing` do RootRoute manteria o
    // gate montado após o navigate (ex.: Voltar da tela needsPreference).
    setPairingFinalizing(false)
    navigate('/')
  }, [navigate, setPairingFinalizing])

  // Escape do 'awaitingSetup': para a espera e cai no fluxo de configurar fonte
  // na própria TV. Como já fizemos loginSwipeSession (há sessão Swipe) e ainda
  // não há fonte/isAuthed, o RootRoute (caso 3) leva a /onboarding/mode|/login|
  // /direct — o caminho logado de configurar fonte que já existe hoje.
  const configureHere = useCallback(() => {
    abortRef.current?.abort()
    // Mesmo motivo do cancel: solta a guarda pra o RootRoute rotear pra config.
    setPairingFinalizing(false)
    navigate('/', { replace: true })
  }, [navigate, setPairingFinalizing])

  const retry = useCallback(() => {
    abortRef.current?.abort()
    // Reinício limpo: o finalizeLogin religa o flag se chegar de novo ao finalize.
    setPairingFinalizing(false)
    setStage({ kind: 'loading' })
    setAttempt((n) => n + 1)
  }, [setPairingFinalizing])

  // Init + polling — re-roda quando attempt incrementa
  useEffect(() => {
    const ctrl = new AbortController()
    abortRef.current = ctrl
    let cancelled = false

    const finalizeLogin = async (swipeAccessToken: string) => {
      // Marca o pareamento ATIVO no gate antes de qualquer mutação do store: dali
      // pra frente o RootRoute NÃO pode redirecionar por baixo do gate (isso o
      // desmonta e aborta este finalize). Durante o finalize o store muda em
      // passos (setProvider → api.login → setAuth), cada um re-renderizando o
      // RootRoute enquanto isAuthed ainda não é true; sem o flag, esses
      // re-renders navegavam pra fora (bug: TV caía em user/senha Xtream após o QR
      // aprovado). O flag NÃO é desligado aqui de propósito: no SUCESSO o próprio
      // `setAuth` já o desliga (e o RootRoute cai no caso 1 → /profiles); no
      // preference_not_set/erro o gate PERMANECE montado exibindo a tela
      // needsPreference/erro (mantê-lo requer o flag ligado — senão o RootRoute
      // re-renderiza e joga o usuário na config de fonte sem ele ver a mensagem).
      // O desligamento nesses casos vem da ação do usuário (cancel/configureHere/
      // retry) ou do cleanup no unmount.
      setPairingFinalizing(true)
      try {
        // A sequência resolve → branding → setProvider → api.login → setAuth mora em
        // `hydrateSourceFromAccount` (account-context.ts), fonte única do contexto de
        // sync: a superfície mobile usa a MESMA, e era a falta dela lá que deixava o
        // celular sem sync nenhum. A ORDEM dos sets e os dois pontos de cancelamento
        // (depois do branding, depois do login) são os de sempre: o `setAuth` liga
        // isAuthed=true E desliga pairingFinalizing no MESMO set() (átomo), e o próximo
        // render cai no caso 1 do RootRoute → /profiles.
        const outcome = await hydrateSourceFromAccount({
          resolve: () => api.swipePreferenceResolve(swipeAccessToken),
          confirm: {
            kind: 'authenticate',
            // Reusa o fluxo Xtream existente — popula o auth-store.
            login: (pref) => api.login(pref.providerCode, pref.xtreamUsername, pref.xtreamPassword),
          },
          isCancelled: () => cancelled,
        })
        if (outcome === 'cancelled' || cancelled) return
        navigate('/', { replace: true })
      } catch (err) {
        if (cancelled) return
        if (err instanceof ApiError && err.code === 'preference_not_set') {
          // Gate segue montado mostrando needsPreference (flag ainda ligado).
          // O usuário decide via botão Voltar (onCancel → cancel(), que desliga
          // o flag e navega pro caso 3 = configurar fonte).
          setStage({ kind: 'needsPreference' })
          return
        }
        throw err
      }
    }

    const run = async () => {
      try {
        const init = await api.swipePairingInit(
          t('account.login.deviceLabel') || 'Smart TV',
        )
        if (cancelled) return
        const expiresAt = new Date(init.expiresAt).getTime()
        setStage({
          kind: 'waiting',
          code: init.code,
          qrPayload: init.qrPayload,
          expiresAt,
        })

        const result = await pollPairingStatus(init.code, ctrl.signal)
        if (cancelled) return

        if (result.status === 'denied') {
          setStage({ kind: 'error', message: t('account.login.denied'), recoverable: true })
          return
        }
        if (result.status === 'expired') {
          setStage({ kind: 'error', message: t('account.login.expired'), recoverable: true })
          return
        }

        // approved — persiste a sessão Swipe (refresh 30d) pra o SYNC sobreviver
        // ao reload. Antes o token era descartado após o resolve; agora mantemos
        // a conta Swipe viva pra "continuar de onde parou" cross-device. Se for
        // OUTRA conta Swipe, loginSwipeSession limpa o sync da anterior.
        const persisted = await loginSwipeSession(result.accessToken, result.refreshToken)
        // O `if (cancelled) return` que ficava AQUI desceu pra depois do bloco
        // `!persisted`, e a ordem é deliberada: o abandono (gate desmontado /
        // cancel / retry) é justamente o caso em que ninguém mais vai usar NEM
        // revogar este token. Retornar antes de olhar `persisted` deixava o par
        // aprovado válido no servidor sem nenhum dono. O guard continua
        // cobrindo todo o resto do fluxo, e o caminho de SUCESSO segue idêntico
        // (nada é revogado quando `persisted === true`).

        // A sessão Swipe NÃO foi pro disco (token aprovado sem `swipeUserId`).
        // Este retorno era IGNORADO: seguíamos pro finalizeLogin, que faz
        // `setAuth` — e o aparelho terminava o pareamento "logado" (isAuthed=true,
        // Xtream resolvido) e SEM sessão Swipe em disco. Esse é exatamente o
        // estado que o RootRoute (I6) manda pro GATE DE VÍNCULO em TODO boot
        // seguinte: o usuário usa o app hoje, fecha, reabre e cai no pareamento,
        // com a sessão Xtream intacta e inalcançável. Não há volta (só parear de
        // novo ou "Sair da conta"), e nada avisa que a conta não foi salva.
        //
        // Falhar AQUI, retryável e visível, é estritamente melhor: nada foi
        // autenticado ainda (setAuth não rodou), o usuário gera outro código e o
        // aparelho nunca entra no estado sem volta. Invariante que este guard
        // sustenta: *só entra no app quem tem a sessão Swipe em disco* — o mesmo
        // que o boot exige pra deixar entrar. Travado em
        // `session-rehydrate.test.tsx`.
        if (!persisted) {
          // O par de tokens foi APROVADO no celular e nunca vai ser usado: o
          // fluxo morre aqui e nada dele saiu da memória deste device. Sem
          // revogar, ele seguiria VÁLIDO no servidor até expirar (refresh de
          // 30d), como uma sessão fantasma que o usuário não consegue ver nem
          // encerrar — o "Sair" da conta só alcança sessão que existe em disco.
          //
          // `void` + best-effort de propósito (ver api.swipeLogout): a UI já
          // está indo pra tela de erro retryável e não pode ficar refém da rede
          // pra isso. Se a revogação falhar, o token só expira sozinho — que é
          // exatamente o estado de hoje, nunca pior.
          //
          // Ordem importa: dispara ANTES do `if (cancelled)` porque o token
          // precisa ser revogado mesmo se o gate desmontou nesse meio-tempo
          // (aí sim ninguém mais tem como fazê-lo).
          //
          // O `.catch` aqui é redundante com o de `api.swipeLogout` — e fica de
          // propósito. `void <promise>` sem catch é frágil por construção: no
          // dia em que alguém tirar o catch de lá (ou trocar a implementação),
          // isto vira unhandled rejection num caminho de erro, e o `try/catch`
          // do `run()` não pega promise solta. Custa uma linha; a alternativa é
          // um bug que só aparece em produção, no sofá.
          void api.swipeLogout(result.accessToken, result.refreshToken).catch(() => undefined)
          if (cancelled) return
          setStage({
            kind: 'error',
            message: t('account.login.sessionNotPersisted'),
            recoverable: true,
          })
          return
        }
        // Sessão em disco: daqui pra frente o token TEM dono (o boot seguinte o
        // reidrata), então abandonar não deixa nada órfão — só sair.
        if (cancelled) return

        // P2 — a TV AGUARDA o setup. Checa o readiness da conta UMA vez:
        // - JÁ tem fonte (base instalada / re-pareamento / configurou antes de
        //   voltar pra TV) → segue DIRETO pro finalizeLogin, IDÊNTICO a hoje.
        //   Não introduz tela de espera pra quem já está pronto.
        // - SEM fonte → entra em 'awaitingSetup' e polla o setup-status até a
        //   fonte ser salva no celular/site; só então finaliza. (não-regressão:
        //   a espera SÓ dispara quando hasSource=false.)
        let hasSource = false
        try {
          const initialStatus = await api.swipeSetupStatus(result.accessToken)
          hasSource = initialStatus.hasSource
        } catch {
          // Readiness indisponível (rede/erro): não trava o usuário com fonte.
          // Cai no finalizeLogin, que faz o resolve e decide (sucesso → app;
          // preference_not_set → needsPreference, como antes).
          hasSource = true
        }
        if (cancelled) return

        if (hasSource) {
          setStage({ kind: 'finalizing' })
          await finalizeLogin(result.accessToken)
          return
        }

        // Sem fonte: aguarda o celular/site. UI mostra "Configurando no seu
        // celular..." com escape "Configurar aqui na TV".
        setStage({ kind: 'awaitingSetup' })
        const setupResult = await pollAccountSetup(result.accessToken, ctrl.signal)
        if (cancelled) return
        if ('status' in setupResult) {
          // TTL estourado sem fonte: cai no fluxo de configurar na própria TV
          // (o escape automático). Há sessão Swipe sem fonte → RootRoute caso 3.
          navigate('/', { replace: true })
          return
        }
        // Fonte salva no celular/site → finaliza e entra no app.
        setStage({ kind: 'finalizing' })
        await finalizeLogin(result.accessToken)
      } catch (err) {
        if (cancelled) return
        if ((err as { name?: string }).name === 'AbortError') return
        setStage({
          kind: 'error',
          message: errorToMessage(err, t),
          recoverable: true,
        })
      }
    }

    void run()
    return () => {
      cancelled = true
      ctrl.abort()
      // Defensivo: se o gate desmontar com o flag AINDA ligado (finalize num
      // await, ou parado em needsPreference/error e o usuário saiu por um caminho
      // que não passou por cancel/configureHere/retry), solta o flag pra ele não
      // ficar preso e travar o RootRoute no gate. Lê o estado atual via getState
      // (não closure). No sucesso o setAuth já desligou → aqui é no-op.
      if (useSessionStore.getState().pairingFinalizing) {
        useSessionStore.getState().setPairingFinalizing(false)
      }
    }
    // `attempt` força re-execução em retry. `t` mudaria em troca de idioma
    // — re-gera código (aceitamos custo: 1 código órfão por troca).
    // navigate é estável nessa lib (os sets do store vivem no account-context).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt])

  return { stage, retry, cancel, configureHere }
}
